import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleCheck,
  Clipboard,
  Clock3,
  Filter,
  Plus,
  Link2,
  Loader2,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Tag as TagIcon,
  Trash2,
  Wand2,
  Webhook,
} from 'lucide-react';
import { toast } from 'react-toastify';

import ConfirmDeleteModal from '../components/ConfirmDeleteModal.tsx';
import { getDashboardThemeClasses } from '../components/dashboard/dashboardTheme.ts';
import FormOfficialTemplateSettings, { useFormOfficialTemplates, validateFormOfficialTemplate } from '../components/FormOfficialTemplateSettings.tsx';
import TagSelector from '../components/TagSelector.tsx';
import { CRM_SOURCE_OPTIONS, DEFAULT_CRM_SOURCE } from '../constants/sourceOptions.ts';
import { useTheme } from '../contexts/ThemeContext.tsx';
import {
  FormLeadFieldMapping,
  FormLeadMessageConfig,
  FormLeadWebhookConfig,
  FormLeadWebhookListResponse,
  FormLeadWebhookTestResult,
  createNewFormLeadWebhook,
  getFormLeadWebhookById,
  listFormLeadWebhooks,
  revokeFormLeadWebhookById,
  rotateFormLeadWebhookById,
  testFormLeadWebhookMapping,
  updateFormLeadWebhookById,
} from '../services/api.ts';

type MappingKey =
  | 'name'
  | 'phone';

interface InternalField {
  key: MappingKey;
  label: string;
  required?: boolean;
  aliases: string[];
}

interface PayloadOption {
  path: string;
  value: string;
  label: string;
  sourceLabel?: string;
  fieldType?: string;
}

const INTERNAL_FIELDS: InternalField[] = [
  { key: 'name', label: 'Nome do paciente', required: true, aliases: ['name', 'nome', 'full_name', 'fullname', 'cliente', 'paciente'] },
  { key: 'phone', label: 'WhatsApp', required: true, aliases: ['phone', 'telefone', 'whatsapp', 'celular', 'mobile', 'fone'] },
];

const DEFAULT_MESSAGE_CONFIG: FormLeadMessageConfig = {
  send_initial_message: true,
  campaign_context: '',
  tag_ids: [],
};

const EMPTY_MAPPING: FormLeadFieldMapping = {
  name: '',
  phone: '',
  source_id_static: DEFAULT_CRM_SOURCE,
};

const TECHNICAL_PAYLOAD_KEYS = new Set(['id', 'type', 'title', 'required', 'raw_value']);

const FIELD_TYPE_LABELS: Record<string, string> = {
  tel: 'WhatsApp',
  phone: 'WhatsApp',
  whatsapp: 'WhatsApp',
  email: 'E-mail',
  url: 'Site',
};

function compactMapping(mapping: FormLeadFieldMapping): FormLeadFieldMapping {
  const compacted: FormLeadFieldMapping = {
    name: mapping.name?.trim() || '',
    phone: mapping.phone?.trim() || '',
  };

  const staticSource = mapping.source_id_static?.trim();
  if (staticSource) {
    compacted.source_id_static = staticSource;
  } else if (mapping.source_id?.trim()) {
    compacted.source_id = mapping.source_id.trim();
  }

  if (mapping.interest?.trim()) compacted.interest = mapping.interest.trim();
  if (mapping.message?.trim()) compacted.message = mapping.message.trim();

  return compacted;
}

export function compactMessageConfig(config: FormLeadMessageConfig): FormLeadMessageConfig {
  const tagIds = Array.from(new Set((config.tag_ids || []).filter(tagId => Number.isInteger(tagId) && tagId > 0))).slice(0, 10);
  return {
    send_initial_message: config.send_initial_message !== false,
    campaign_context: config.campaign_context?.trim() || '',
    ...(tagIds.length ? { tag_ids: tagIds } : {}),
    ...(config.official_template_id === undefined ? {} : { official_template_id: config.official_template_id }),
    ...(config.official_template_parameters === undefined ? {} : {
      official_template_parameters: Object.fromEntries(Object.entries(config.official_template_parameters).map(([key, binding]) => [key, {
        source: binding.source,
        ...(binding.source === 'literal' ? { value: binding.value?.trim() || '' } : {}),
      }])),
    }),
  };
}

function isScalar(value: unknown): boolean {
  return ['string', 'number', 'boolean'].includes(typeof value);
}

function stringifyValue(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map(item => String(item)).join(', ');
  }
  return String(value ?? '');
}

function coerceDisplayValue(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  return stringifyValue(value).trim();
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function normalizeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function humanizeKey(key: string): string {
  const cleaned = key
    .replace(/^field_/, 'Campo ')
    .replace(/[_-]+/g, ' ')
    .trim();
  if (!cleaned) {
    return 'Campo';
  }
  return cleaned.replace(/\b\w/g, char => char.toUpperCase());
}

function isSelectableValue(value: unknown): boolean {
  if (isScalar(value)) {
    return coerceDisplayValue(value).length > 0;
  }
  return Array.isArray(value) && value.length > 0 && value.every(isScalar);
}

function buildOptionLabel(sourceLabel: string, value: string): string {
  return `${sourceLabel} - ${value}`;
}

function appendOption(options: PayloadOption[], option: PayloadOption, limit: number): void {
  if (options.length >= limit || !option.value || options.some(item => item.path === option.path)) {
    return;
  }
  options.push(option);
}

function fieldDisplayName(fieldKey: string, field: Record<string, unknown>): string {
  const title = coerceDisplayValue(field.title);
  if (title) {
    return title;
  }

  const type = normalizeText(coerceDisplayValue(field.type));
  if (FIELD_TYPE_LABELS[type]) {
    return FIELD_TYPE_LABELS[type];
  }

  return humanizeKey(fieldKey);
}

function isStructuredFormField(value: unknown): value is Record<string, unknown> {
  const field = asRecord(value);
  return Boolean(field && (Object.prototype.hasOwnProperty.call(field, 'value') || Object.prototype.hasOwnProperty.call(field, 'raw_value')));
}

function extractStructuredFormOptions(payload: unknown, limit: number): PayloadOption[] {
  const root = asRecord(payload);
  const fields = asRecord(root?.fields);
  if (!fields) {
    return [];
  }

  const options: PayloadOption[] = [];
  Object.entries(fields).forEach(([fieldKey, fieldValue]) => {
    if (options.length >= limit || !isStructuredFormField(fieldValue)) {
      return;
    }

    const preferredKey = isSelectableValue(fieldValue.value) ? 'value' : 'raw_value';
    const rawValue = fieldValue[preferredKey];
    if (!isSelectableValue(rawValue)) {
      return;
    }

    const value = coerceDisplayValue(rawValue);
    const sourceLabel = fieldDisplayName(fieldKey, fieldValue);
    const fieldType = coerceDisplayValue(fieldValue.type);
    appendOption(options, {
      path: `fields.${fieldKey}.${preferredKey}`,
      value,
      label: buildOptionLabel(sourceLabel, value),
      sourceLabel,
      fieldType,
    }, limit);
  });

  return options;
}

function shouldSkipScalarPath(path: string): boolean {
  const parts = path.split(/[.[\]]/).filter(Boolean);
  const lastPart = normalizeText(parts[parts.length - 1] || '');
  return path.startsWith('form.') || TECHNICAL_PAYLOAD_KEYS.has(lastPart);
}

function flattenPayload(payload: unknown, prefix = '', limit = 80): PayloadOption[] {
  if (!payload || typeof payload !== 'object') {
    return [];
  }

  const structuredOptions = extractStructuredFormOptions(payload, limit);
  if (structuredOptions.length > 0) {
    return structuredOptions;
  }

  const options: PayloadOption[] = [];
  const visit = (value: unknown, path: string) => {
    if (options.length >= limit || value === null || value === undefined) {
      return;
    }

    if (isScalar(value)) {
      if (!shouldSkipScalarPath(path)) {
        const displayValue = coerceDisplayValue(value);
        const sourceLabel = humanizeKey(path.split('.').pop() || path);
        appendOption(options, {
          path,
          value: displayValue,
          label: buildOptionLabel(sourceLabel, displayValue),
          sourceLabel,
        }, limit);
      }
      return;
    }

    if (Array.isArray(value)) {
      if (value.length > 0 && value.every(isScalar)) {
        const displayValue = coerceDisplayValue(value);
        const sourceLabel = humanizeKey(path.split('.').pop() || path);
        appendOption(options, {
          path,
          value: displayValue,
          label: buildOptionLabel(sourceLabel, displayValue),
          sourceLabel,
        }, limit);
        return;
      }
      value.slice(0, 8).forEach((item, index) => visit(item, `${path}[${index}]`));
      return;
    }

    if (isStructuredFormField(value)) {
      const preferredKey = isSelectableValue(value.value) ? 'value' : 'raw_value';
      const rawValue = value[preferredKey];
      if (isSelectableValue(rawValue)) {
        const displayValue = coerceDisplayValue(rawValue);
        const fieldKey = path.split('.').pop() || path;
        const sourceLabel = fieldDisplayName(fieldKey, value);
        appendOption(options, {
          path: `${path}.${preferredKey}`,
          value: displayValue,
          label: buildOptionLabel(sourceLabel, displayValue),
          sourceLabel,
          fieldType: coerceDisplayValue(value.type),
        }, limit);
      }
      return;
    }

    Object.entries(value as Record<string, unknown>).forEach(([key, child]) => {
      visit(child, path ? `${path}.${key}` : key);
    });
  };

  visit(payload, prefix);
  return options;
}

function getPayloadValueByPath(payload: unknown, path?: string): unknown {
  if (!path) {
    return undefined;
  }

  let current: unknown = payload;
  const parts = path.match(/[^.[\]]+|\[\d+\]/g) || [];
  for (const part of parts) {
    if (part.startsWith('[') && part.endsWith(']')) {
      const index = Number(part.slice(1, -1));
      if (!Array.isArray(current) || index >= current.length) {
        return undefined;
      }
      current = current[index];
      continue;
    }

    const currentRecord = asRecord(current);
    if (!currentRecord || !Object.prototype.hasOwnProperty.call(currentRecord, part)) {
      return undefined;
    }
    current = currentRecord[part];
  }

  return current;
}

function pathHasUsableValue(payload: unknown, path?: string): boolean {
  return coerceDisplayValue(getPayloadValueByPath(payload, path)).length > 0;
}

function scoreOption(field: InternalField, option: PayloadOption): number {
  const haystack = normalizeText(`${option.path} ${option.value} ${option.label} ${option.sourceLabel || ''} ${option.fieldType || ''}`);
  let score = 0;

  field.aliases.forEach(alias => {
    if (haystack.includes(normalizeText(alias))) {
      score += 12;
    }
  });

  const normalizedType = normalizeText(option.fieldType || '');
  if (field.key === 'phone' && option.value.replace(/\D/g, '').length >= 10) {
    score += 30;
  }
  if (field.key === 'phone' && ['tel', 'phone', 'whatsapp'].includes(normalizedType)) {
    score += 40;
  }
  if (field.key === 'name' && /^[A-Za-zÀ-ÿ]+ [A-Za-zÀ-ÿ]+/.test(option.value)) {
    score += 8;
  }
  return score;
}

function suggestMapping(options: PayloadOption[], current: FormLeadFieldMapping, payload: unknown): FormLeadFieldMapping {
  const next = { ...current };
  INTERNAL_FIELDS.forEach(field => {
    const currentPath = next[field.key];
    if (currentPath && pathHasUsableValue(payload, currentPath)) {
      return;
    }

    const best = options
      .map(option => ({ option, score: scoreOption(field, option) }))
      .sort((a, b) => b.score - a.score)[0];

    if (best && best.score >= 12) {
      next[field.key] = best.option.path;
    } else if (currentPath) {
      next[field.key] = '';
    }
  });
  return next;
}

function formatDateTime(value?: string | null): string {
  if (!value) {
    return '';
  }
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function readSelectedClinicId(): number | null {
  const clinicId = Number(localStorage.getItem('clinic_id') || 0);
  return Number.isFinite(clinicId) && clinicId > 0 ? clinicId : null;
}

const FormLeadWebhookPage: React.FC = () => {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingLink, setCreatingLink] = useState(false);
  const [testing, setTesting] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [view, setView] = useState<'list' | 'editor'>('list');
  const [isCreatingWebhook, setIsCreatingWebhook] = useState(false);
  const [selectedWebhookId, setSelectedWebhookId] = useState<number | null>(null);
  const [webhookRows, setWebhookRows] = useState<FormLeadWebhookConfig[]>([]);
  const [listSummary, setListSummary] = useState<FormLeadWebhookListResponse['summary']>({
    total: 0,
    active: 0,
    setup: 0,
    leads_last_30_days: 0,
  });
  const [listTotal, setListTotal] = useState(0);
  const [listSearch, setListSearch] = useState('');
  const [listStatus, setListStatus] = useState<'all' | 'active' | 'setup' | 'revoked'>('all');
  const [listPage, setListPage] = useState(0);
  const [listError, setListError] = useState<string | null>(null);
  const [webhookConfig, setWebhookConfig] = useState<FormLeadWebhookConfig | null>(null);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [clinicId, setClinicId] = useState<number | null>(() => readSelectedClinicId());
  const [name, setName] = useState('');
  const [fieldMapping, setFieldMapping] = useState<FormLeadFieldMapping>(EMPTY_MAPPING);
  const [messageConfig, setMessageConfig] = useState<FormLeadMessageConfig>(DEFAULT_MESSAGE_CONFIG);
  const [testResult, setTestResult] = useState<FormLeadWebhookTestResult['extracted'] | null>(null);
  const [autoMappedCaptureId, setAutoMappedCaptureId] = useState<number | null>(null);
  const [confirmRotateOpen, setConfirmRotateOpen] = useState(false);
  const [confirmRevokeOpen, setConfirmRevokeOpen] = useState(false);
  const officialCatalog = useFormOfficialTemplates(clinicId);

  const theme = getDashboardThemeClasses(isDark);
  const pageBg = isDark ? theme.canvas : 'bg-sminy-canvas text-sminy-text-primary';
  const pageSpacing = 'px-4 pb-[calc(6rem+env(safe-area-inset-bottom))] pt-4 font-[Lexend_Deca] sm:p-7 sm:pb-12';
  const panel = theme.surface;
  const input = `${theme.input} placeholder:text-sminy-muted focus:border-sminy-primary focus:ring-sminy-primary`;
  const mutedText = theme.mutedText;
  const buttonBase = 'inline-flex min-h-10 items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
  const primaryButton = `${buttonBase} bg-sminy-primary text-white hover:bg-sminy-primary-hover active:bg-sminy-primary-pressed`;
  const secondaryButton = `${buttonBase} ${theme.control}`;
  const outlineButton = `${buttonBase} border border-sminy-primary text-sminy-primary hover:bg-sminy-primary/10`;
  const destructiveButton = `${buttonBase} border border-rose-500/30 text-rose-500 hover:bg-rose-500/10`;
  const capturedPayload = webhookConfig?.last_captured_payload || null;
  const isActive = webhookConfig?.status === 'active';
  const captureStatus = webhookConfig?.last_capture_status || null;
  const hasMappingIssue = captureStatus === 'needs_mapping';

  const loadWebhookList = useCallback(async () => {
    const selectedClinicId = readSelectedClinicId();
    if (!selectedClinicId) {
      throw new Error('Não foi possível determinar a clínica selecionada.');
    }
    setClinicId(selectedClinicId);
    const response = await listFormLeadWebhooks(selectedClinicId, {
      limit: 25,
      offset: listPage * 25,
      search: listSearch.trim() || undefined,
      status: listStatus === 'all' ? undefined : listStatus,
    });
    setWebhookRows(response.webhooks || []);
    setListTotal(response.total || 0);
    setListSummary(response.summary || { total: 0, active: 0, setup: 0, leads_last_30_days: 0 });
    setListError(null);
    return response;
  }, [listPage, listSearch, listStatus]);

  const loadWebhook = useCallback(async (webhookId: number) => {
    const selectedClinicId = readSelectedClinicId();
    if (!selectedClinicId) {
      throw new Error('Não foi possível determinar a clínica selecionada.');
    }
    setClinicId(selectedClinicId);
    const response = await getFormLeadWebhookById(webhookId, selectedClinicId);
    const webhook = response.webhook;
    setWebhookConfig(webhook);
    setGeneratedUrl(webhook?.webhook_url || null);
    if (webhook) {
      setName(webhook.name || 'Formulário');
      setFieldMapping({ ...EMPTY_MAPPING, ...(webhook.field_mapping || {}) });
      const nextMessageConfig = { ...DEFAULT_MESSAGE_CONFIG, ...(webhook.message_config || {}) };
      setMessageConfig(nextMessageConfig);
    } else {
      setMessageConfig({ ...DEFAULT_MESSAGE_CONFIG });
    }
    return webhook;
  }, []);

  const resolveClinicIdForAction = useCallback((): number | null => {
    const selectedClinicId = readSelectedClinicId();
    if (!selectedClinicId) {
      toast.error('Não foi possível determinar a clínica selecionada.');
      return null;
    }
    setClinicId(selectedClinicId);
    return selectedClinicId;
  }, []);

  const openNewWebhook = () => {
    setIsCreatingWebhook(true);
    setSelectedWebhookId(null);
    setWebhookConfig(null);
    setGeneratedUrl(null);
    setName('');
    setFieldMapping(EMPTY_MAPPING);
    setMessageConfig({ ...DEFAULT_MESSAGE_CONFIG });
    setTestResult(null);
    setAutoMappedCaptureId(null);
    setView('editor');
  };

  const openWebhook = async (webhookId: number) => {
    setView('editor');
    setIsCreatingWebhook(false);
    setSelectedWebhookId(webhookId);
    setLoading(true);
    setTestResult(null);
    setAutoMappedCaptureId(null);
    try {
      await loadWebhook(webhookId);
    } catch (error: any) {
      toast.error(error.message || 'Não foi possível carregar este webhook.');
      setView('list');
    } finally {
      setLoading(false);
    }
  };

  const returnToList = () => {
    setView('list');
    setIsCreatingWebhook(false);
    setSelectedWebhookId(null);
    setWebhookConfig(null);
    setGeneratedUrl(null);
  };

  useEffect(() => {
    if (view !== 'list') return;
    setLoading(true);
    loadWebhookList()
      .catch((error: any) => {
        setListError(error.message || 'Não foi possível carregar os webhooks.');
        toast.error(error.message || 'Não foi possível carregar os webhooks.');
      })
      .finally(() => setLoading(false));
  }, [loadWebhookList, view]);

  const activePayload = capturedPayload;
  const payloadOptions = useMemo(() => flattenPayload(activePayload), [activePayload]);

  useEffect(() => {
    const captureId = webhookConfig?.last_capture_event_id || null;
    if (!captureId || captureId === autoMappedCaptureId || payloadOptions.length === 0) {
      return;
    }
    setFieldMapping(prev => suggestMapping(payloadOptions, prev, activePayload));
    setAutoMappedCaptureId(captureId);
  }, [activePayload, autoMappedCaptureId, payloadOptions, webhookConfig?.last_capture_event_id]);

  const updateMapping = (key: MappingKey, value: string) => {
    setFieldMapping(prev => ({ ...prev, [key]: value }));
  };

  const getOptionsForField = (key: MappingKey): PayloadOption[] => {
    const currentPath = fieldMapping[key];
    if (!currentPath || payloadOptions.some(option => option.path === currentPath)) {
      return payloadOptions;
    }
    const currentValue = coerceDisplayValue(getPayloadValueByPath(activePayload, currentPath));
    if (!currentValue) {
      return payloadOptions;
    }
    return [{
      path: currentPath,
      value: currentValue,
      label: buildOptionLabel('Campo atual', currentValue),
      sourceLabel: 'Campo atual',
    }, ...payloadOptions];
  };

  const handleCreateLink = async () => {
    const selectedClinicId = resolveClinicIdForAction();
    if (!selectedClinicId) {
      return;
    }
    if (!name.trim()) {
      toast.warning('Informe o nome da campanha.');
      return;
    }
    setCreatingLink(true);
    try {
      const response = await createNewFormLeadWebhook(
        name.trim(),
        compactMessageConfig(messageConfig),
        selectedClinicId,
      );
      setWebhookConfig(response.webhook);
      setSelectedWebhookId(response.webhook?.id || null);
      setIsCreatingWebhook(false);
      if (response.webhook?.webhook_url) {
        setGeneratedUrl(response.webhook.webhook_url);
      }
      await loadWebhookList();
      toast.success('Webhook criado.');
    } catch (error: any) {
      toast.error(error.message || 'Não foi possível criar o webhook.');
    } finally {
      setCreatingLink(false);
    }
  };

  const handleRefreshCapture = async () => {
    if (!selectedWebhookId) return;
    setRefreshing(true);
    try {
      const webhook = await loadWebhook(selectedWebhookId);
      if (webhook?.last_captured_payload && webhook.last_capture_status === 'needs_mapping') {
        toast.warning('Recebemos o envio. Ajuste os campos para criar o lead.');
      } else if (webhook?.last_captured_payload) {
        toast.success('Teste recebido.');
      } else {
        toast.info('Nenhum teste recebido ainda.');
      }
    } catch (error: any) {
      toast.error(error.message || 'Não foi possível buscar o teste.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleCopy = async () => {
    if (!generatedUrl) return;
    try {
      await navigator.clipboard.writeText(generatedUrl);
      toast.success('Link copiado.');
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  };

  const handleValidateMapping = async (clinicIdOverride?: number) => {
    const selectedClinicId = clinicIdOverride || resolveClinicIdForAction();
    if (!selectedClinicId) {
      return false;
    }
    if (!activePayload) {
      toast.warning('Receba um teste antes de validar.');
      return false;
    }
    const compacted = compactMapping(fieldMapping);
    if (!compacted.name || !compacted.phone) {
      toast.warning('Selecione nome e WhatsApp antes de ativar.');
      return false;
    }

    setTesting(true);
    setTestResult(null);
    try {
      const response = await testFormLeadWebhookMapping(activePayload, compacted, selectedClinicId);
      setTestResult(response.extracted);
      toast.success('Campos validados.');
      return true;
    } catch (error: any) {
      toast.error(error.message || 'Não foi possível validar os campos.');
      return false;
    } finally {
      setTesting(false);
    }
  };

  const handleActivate = async () => {
    const selectedClinicId = resolveClinicIdForAction();
    if (!selectedClinicId) {
      return;
    }
    if (selectedClinicId !== clinicId || officialCatalog.clinicId !== selectedClinicId) {
      toast.warning('A clínica selecionada mudou. Recarregue a página antes de salvar.');
      return;
    }
    const messageError = validateFormOfficialTemplate(messageConfig, officialCatalog, fieldMapping);
    if (messageError) {
      toast.warning(messageError);
      return;
    }
    const isValid = await handleValidateMapping(selectedClinicId);
    if (!isValid) {
      return;
    }

    setSaving(true);
    try {
      if (!selectedWebhookId) {
        toast.warning('Crie o webhook antes de ativar a integração.');
        return;
      }
      const response = await updateFormLeadWebhookById(selectedWebhookId, {
        name: name.trim() || 'Formulário',
        field_mapping: compactMapping(fieldMapping),
        message_config: compactMessageConfig(messageConfig),
      }, selectedClinicId);
      setWebhookConfig(response.webhook);
      if (response.webhook?.webhook_url) {
        setGeneratedUrl(response.webhook.webhook_url);
      }
      await loadWebhookList();
      toast.success('Integração ativada.');
    } catch (error: any) {
      toast.error(error.message || 'Não foi possível ativar a integração.');
    } finally {
      setSaving(false);
    }
  };

  const handleRotate = async () => {
    const selectedClinicId = resolveClinicIdForAction();
    if (!selectedClinicId || !selectedWebhookId) {
      return;
    }
    setRotating(true);
    try {
      const response = await rotateFormLeadWebhookById(selectedWebhookId, selectedClinicId);
      setWebhookConfig(response.webhook);
      setGeneratedUrl(response.webhook?.webhook_url || null);
      await loadWebhookList();
      toast.success('Link regenerado.');
    } catch (error: any) {
      toast.error(error.message || 'Não foi possível regenerar o link.');
    } finally {
      setRotating(false);
    }
  };

  const handleRevoke = async () => {
    const selectedClinicId = resolveClinicIdForAction();
    if (!selectedClinicId || !selectedWebhookId) {
      return;
    }
    setRevoking(true);
    try {
      await revokeFormLeadWebhookById(selectedWebhookId, selectedClinicId);
      setWebhookConfig(null);
      setGeneratedUrl(null);
      setFieldMapping(EMPTY_MAPPING);
      setMessageConfig({ ...DEFAULT_MESSAGE_CONFIG });
      setTestResult(null);
      setView('list');
      setIsCreatingWebhook(false);
      setSelectedWebhookId(null);
      toast.success('Webhook revogado.');
    } catch (error: any) {
      toast.error(error.message || 'Não foi possível revogar o webhook.');
    } finally {
      setRevoking(false);
    }
  };

  const renderStepStatus = (complete: boolean) => (
    <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
      complete
        ? 'bg-sminy-primary text-white'
        : `${theme.track} ${isDark ? 'text-[#A6B2C3]' : 'text-sminy-muted'}`
    }`}>
      {complete ? <CheckCircle2 className="h-4 w-4" /> : null}
    </span>
  );

  const statusLabel: Record<string, string> = {
    active: 'Ativo',
    setup: 'Configuração pendente',
    revoked: 'Revogado',
  };
  const statusClass: Record<string, string> = {
    active: 'border-sminy-success/30 bg-sminy-success/10 text-sminy-success',
    setup: isDark ? 'border-amber-300/25 bg-amber-300/10 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-700',
    revoked: isDark ? 'border-slate-500/40 bg-slate-700/40 text-slate-300' : 'border-slate-200 bg-slate-50 text-slate-600',
  };
  const pageCount = Math.max(1, Math.ceil(listTotal / 25));

  if (view === 'list') {
    if (loading) {
      return (
        <div className={`min-h-screen ${pageBg} ${pageSpacing}`}>
          <div className="mx-auto max-w-[1440px] space-y-5" aria-label="Carregando webhooks">
            <div className={`h-24 animate-pulse rounded-xl ${theme.skeleton}`} />
            <div className={`h-28 animate-pulse rounded-xl ${theme.skeleton}`} />
            <div className={`h-96 animate-pulse rounded-xl ${theme.skeleton}`} />
          </div>
        </div>
      );
    }

    return (
      <div className={`min-h-screen ${pageBg} ${pageSpacing}`}>
        <div className="mx-auto max-w-[1440px] space-y-5">
          <header className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-sminy-primary">Integrações</p>
              <h1 className="text-2xl font-medium tracking-tight">Webhooks de formulários</h1>
              <p className={`mt-1.5 text-sm ${mutedText}`}>Gerencie campanhas, recebimentos e regras de entrada no CRM.</p>
            </div>
            <button type="button" onClick={openNewWebhook} className={primaryButton}>
              <Plus className="h-4 w-4" />
              Novo webhook
            </button>
          </header>

          <section aria-label="Resumo dos webhooks" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { label: 'Webhooks criados', value: listSummary.total, icon: Webhook },
              { label: 'Ativos', value: listSummary.active, icon: CircleCheck },
              { label: 'Leads vinculados · 30 dias', value: listSummary.leads_last_30_days, icon: Clock3 },
            ].map(item => {
              const Icon = item.icon;
              return (
                <div key={item.label} className={`flex min-h-[96px] items-center gap-4 rounded-xl border px-4 py-4 ${panel}`}>
                  <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${theme.accentSurface}`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <div>
                    <p className={`text-sm ${mutedText}`}>{item.label}</p>
                    <p className="mt-1 text-xl font-semibold tabular-nums">{item.value}</p>
                  </div>
                </div>
              );
            })}
          </section>

          <section className={`overflow-hidden rounded-xl border ${panel}`}>
            <div className={`flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center sm:justify-between ${theme.border}`}>
              <div>
                <h2 className="text-base font-semibold">Campanhas conectadas</h2>
                <p className={`mt-1 text-sm ${mutedText}`}>{listTotal} webhook{listTotal === 1 ? '' : 's'} nesta visualização</p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <label className="relative block min-w-0 sm:w-64">
                  <Search className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${mutedText}`} />
                  <input
                    type="search"
                    value={listSearch}
                    onChange={event => { setListSearch(event.target.value); setListPage(0); }}
                    placeholder="Buscar campanha..."
                    aria-label="Buscar campanha"
                    className={`min-h-10 w-full rounded-lg border py-2 pl-9 pr-3 text-sm focus:ring-2 ${input}`}
                  />
                </label>
                <label className="relative block sm:w-48">
                  <Filter className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${mutedText}`} />
                  <select
                    value={listStatus}
                    onChange={event => { setListStatus(event.target.value as typeof listStatus); setListPage(0); }}
                    aria-label="Filtrar por status"
                    className={`min-h-10 w-full appearance-none rounded-lg border py-2 pl-9 pr-3 text-sm focus:ring-2 ${input}`}
                  >
                    <option value="all">Todos os status</option>
                    <option value="active">Ativos</option>
                    <option value="setup">Configuração pendente</option>
                    <option value="revoked">Revogados</option>
                  </select>
                </label>
              </div>
            </div>

            {listError ? (
              <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
                <p className={`text-sm ${mutedText}`}>{listError}</p>
                <button type="button" onClick={() => loadWebhookList().catch(error => setListError(error.message))} className={secondaryButton}>
                  <RefreshCw className="h-4 w-4" /> Tentar novamente
                </button>
              </div>
            ) : listTotal === 0 ? (
              <div className="flex flex-col items-center gap-3 px-4 py-14 text-center">
                <span className={`flex h-12 w-12 items-center justify-center rounded-full ${theme.track}`}>
                  <Webhook className={`h-5 w-5 ${mutedText}`} />
                </span>
                <div>
                  <p className="font-medium">{listSearch || listStatus !== 'all' ? 'Nenhum webhook encontrado' : 'Sua clínica ainda não tem webhooks'}</p>
                  <p className={`mt-1 max-w-md text-sm ${mutedText}`}>
                    {listSearch || listStatus !== 'all' ? 'Altere a busca ou o filtro para ver outras campanhas.' : 'Crie um webhook para conectar o formulário da campanha ao CRM.'}
                  </p>
                </div>
                {!listSearch && listStatus === 'all' && (
                  <button type="button" onClick={openNewWebhook} className={primaryButton}><Plus className="h-4 w-4" /> Novo webhook</button>
                )}
              </div>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[960px] text-left text-sm">
                    <thead className={`${isDark ? 'bg-slate-800/60' : 'bg-slate-50'} ${mutedText}`}>
                      <tr>
                        {['Campanha', 'Origem', 'Status', 'Tags', 'Último recebimento', 'Leads · 30 dias', ''].map((label, index) => (
                          <th key={`${label}-${index}`} scope="col" className="px-4 py-3 text-xs font-medium uppercase tracking-wide">{label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${theme.divide}`}>
                      {webhookRows.map(row => (
                        <tr key={row.id} className={isDark ? 'hover:bg-slate-800/40' : 'hover:bg-slate-50/70'}>
                          <td className="px-4 py-4">
                            <button type="button" onClick={() => openWebhook(row.id)} className="text-left font-medium text-sminy-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary">
                              {row.name}
                            </button>
                            <p className={`mt-1 text-xs ${mutedText}`}>Criado em {formatDateTime(row.created_at) || '—'}</p>
                          </td>
                          <td className={`px-4 py-4 ${mutedText}`}>
                            {row.field_mapping?.source_id_static || (row.field_mapping?.source_id ? 'Do formulário' : 'Não definida')}
                          </td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-medium ${statusClass[row.status] || statusClass.revoked}`}>
                              {statusLabel[row.status] || 'Inativo'}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex items-center gap-1.5 text-sm ${mutedText}`}>
                              <TagIcon className="h-3.5 w-3.5" />
                              {(row.message_config?.tag_ids || []).length || 'Nenhuma'}
                            </span>
                          </td>
                          <td className={`px-4 py-4 ${mutedText}`}>{formatDateTime(row.last_event_at || row.last_captured_at) || 'Sem recebimentos'}</td>
                          <td className="px-4 py-4 font-medium tabular-nums">{row.leads_last_30_days || 0}</td>
                          <td className="px-4 py-4 text-right">
                            <button type="button" onClick={() => openWebhook(row.id)} className={secondaryButton}>Editar</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="divide-y md:hidden">
                  {webhookRows.map(row => (
                    <button key={row.id} type="button" onClick={() => openWebhook(row.id)} className={`block w-full p-4 text-left ${isDark ? 'hover:bg-slate-800/40' : 'hover:bg-slate-50'}`}>
                      <span className="flex items-start justify-between gap-3">
                        <span className="min-w-0">
                          <span className="block truncate font-medium">{row.name}</span>
                          <span className={`mt-1 block text-xs ${mutedText}`}>{formatDateTime(row.last_event_at || row.last_captured_at) || 'Sem recebimentos'}</span>
                        </span>
                        <span className={`shrink-0 rounded-full border px-2 py-1 text-[11px] font-medium ${statusClass[row.status] || statusClass.revoked}`}>
                          {statusLabel[row.status] || 'Inativo'}
                        </span>
                      </span>
                      <span className={`mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs ${mutedText}`}>
                        <span>Origem: {row.field_mapping?.source_id_static || (row.field_mapping?.source_id ? 'Do formulário' : 'Não definida')}</span>
                        <span className="inline-flex items-center gap-1"><TagIcon className="h-3.5 w-3.5" />{(row.message_config?.tag_ids || []).length} tags</span>
                        <span>{row.leads_last_30_days || 0} leads · 30 dias</span>
                      </span>
                    </button>
                  ))}
                </div>

                <div className={`flex items-center justify-between gap-3 border-t px-4 py-3 ${theme.border}`}>
                  <p className={`text-xs ${mutedText}`}>Página {listPage + 1} de {pageCount}</p>
                  <div className="flex gap-2">
                    <button type="button" aria-label="Página anterior" disabled={listPage === 0} onClick={() => setListPage(page => Math.max(0, page - 1))} className={`${secondaryButton} min-h-9 px-3`}>
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button type="button" aria-label="Próxima página" disabled={listPage + 1 >= pageCount} onClick={() => setListPage(page => Math.min(pageCount - 1, page + 1))} className={`${secondaryButton} min-h-9 px-3`}>
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`min-h-screen ${pageBg} ${pageSpacing}`}>
        <div className="mx-auto max-w-[1440px] space-y-5">
          <div className={`h-24 animate-pulse rounded-xl ${theme.skeleton}`} />
          <div className={`h-96 animate-pulse rounded-xl ${theme.skeleton}`} />
        </div>
      </div>
    );
  }

  if (isCreatingWebhook && !webhookConfig) {
    return (
      <div className={`min-h-screen ${pageBg} ${pageSpacing}`}>
        <div className="mx-auto max-w-[960px] space-y-5">
          <button type="button" onClick={returnToList} className={`${secondaryButton} min-h-9 px-3`}>
            <ArrowLeft className="h-4 w-4" /> Voltar para webhooks
          </button>
          <header>
            <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-sminy-primary">Formulários</p>
            <h1 className="text-2xl font-medium tracking-tight">Novo webhook</h1>
            <p className={`mt-1.5 text-sm ${mutedText}`}>Defina a campanha e as tags que serão aplicadas aos leads recebidos.</p>
          </header>
          <section className={`space-y-5 rounded-xl border p-5 sm:p-6 ${panel}`}>
            <label className="block space-y-2">
              <span className={`text-sm font-medium ${theme.secondaryText}`}>Nome da campanha *</span>
              <input
                value={name}
                onChange={event => setName(event.target.value)}
                maxLength={120}
                autoFocus
                className={`min-h-11 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 ${input}`}
                placeholder="Ex.: Avaliação de implantes"
              />
            </label>
            <div className="space-y-2">
              <div>
                <h2 className={`text-sm font-medium ${theme.secondaryText}`}>Tags automáticas</h2>
                <p className={`mt-1 text-xs ${mutedText}`}>As tags escolhidas serão vinculadas aos leads recebidos por este webhook.</p>
              </div>
              {clinicId ? (
                <TagSelector
                  clinicId={clinicId}
                  selectedTags={messageConfig.tag_ids || []}
                  onTagsChange={tagIds => setMessageConfig(current => ({ ...current, tag_ids: tagIds }))}
                  maxTags={10}
                  variant="inline"
                  placeholder="Selecionar tags"
                />
              ) : (
                <p role="alert" className="text-sm text-rose-500">Não foi possível identificar a clínica selecionada.</p>
              )}
            </div>
            <div className={`flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-end ${theme.border}`}>
              <button type="button" onClick={returnToList} className={secondaryButton}>Cancelar</button>
              <button type="button" onClick={handleCreateLink} disabled={creatingLink || !clinicId || !name.trim()} className={primaryButton}>
                {creatingLink ? <Loader2 className="h-4 w-4 animate-spin" /> : <Webhook className="h-4 w-4" />}
                Criar webhook e gerar link
              </button>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${pageBg} ${pageSpacing}`}>
      <div className="mx-auto max-w-[1440px] space-y-5">
        <button type="button" onClick={returnToList} className={`${secondaryButton} min-h-9 px-3`}>
          <ArrowLeft className="h-4 w-4" /> Voltar para webhooks
        </button>
        <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-sminy-primary">Integrações</p>
            <h1 className="text-2xl font-medium tracking-tight">{name || 'Webhook de formulário'}</h1>
            <p className={`mt-1.5 text-sm ${mutedText}`}>Conecte o formulário da campanha ao CRM e confirme os dados antes de ativar.</p>
          </div>
          <div className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium ${
            hasMappingIssue
              ? isDark ? 'border-amber-400/30 bg-amber-500/10 text-amber-200' : 'border-amber-300 bg-amber-50 text-amber-700'
              : isActive ? 'border-sminy-success/30 bg-sminy-success/10 text-sminy-success'
                : webhookConfig?.status === 'revoked' ? statusClass.revoked
                  : 'border-sminy-primary/25 bg-sminy-primary/10 text-sminy-primary'
          }`}>
            {hasMappingIssue ? <AlertTriangle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
            {hasMappingIssue ? 'Ajustar campos' : isActive ? 'Ativo' : webhookConfig?.status === 'revoked' ? 'Revogado' : webhookConfig ? 'Aguardando teste' : 'Não configurado'}
          </div>
        </header>

        <div className="grid gap-4 md:grid-cols-4">
          {[
            ['Link', Boolean(webhookConfig)],
            ['Teste', Boolean(capturedPayload)],
            ['Campos', Boolean(fieldMapping.name && fieldMapping.phone && fieldMapping.source_id_static)],
            ['Ativar', isActive],
          ].map(([label, complete]) => (
            <div key={String(label)} className={`flex items-center gap-3 rounded-xl border p-3 ${panel}`}>
              {renderStepStatus(Boolean(complete))}
              <span className="text-sm font-medium">{label}</span>
            </div>
          ))}
        </div>

        <div className="grid gap-5 lg:grid-cols-[0.95fr_1.05fr]">
          <div className="space-y-5">
            <section className={`rounded-xl border ${panel} p-5`}>
              <div className="flex items-start gap-3">
                <Link2 className="mt-0.5 h-5 w-5 shrink-0 text-sminy-primary" />
                <div className="min-w-0 flex-1">
                  <h2 className="text-sm font-semibold">Link de recebimento</h2>
                  <p className={`mt-1 text-sm ${mutedText}`}>Copie o link e configure no formulário externo.</p>
                </div>
              </div>

              <label className="mt-4 block space-y-2">
                <span className={`text-sm font-medium ${theme.secondaryText}`}>Nome da campanha</span>
                <input
                  value={name}
                  onChange={event => setName(event.target.value)}
                  className={`w-full rounded-xl border p-3 text-sm transition-colors focus:ring-2 ${input}`}
                  placeholder="Ex.: Avaliação de implantes"
                />
              </label>

              <div className="mt-4 space-y-2">
                <div>
                  <h3 className={`text-sm font-medium ${theme.secondaryText}`}>Tags automáticas</h3>
                  <p className={`mt-1 text-xs ${mutedText}`}>Serão vinculadas aos leads que chegarem por esta campanha.</p>
                </div>
                {clinicId && (
                  <TagSelector
                    clinicId={clinicId}
                    selectedTags={messageConfig.tag_ids || []}
                    onTagsChange={tagIds => setMessageConfig(current => ({ ...current, tag_ids: tagIds }))}
                    maxTags={10}
                    variant="inline"
                    placeholder="Selecionar tags"
                  />
                )}
              </div>

              <div className={`mt-4 rounded-xl border p-3 ${theme.input}`}>
                <p className={`break-all font-mono text-xs ${mutedText}`}>
                  {generatedUrl || (webhookConfig?.token_preview ? `Token salvo: ${webhookConfig.token_preview}` : 'Nenhum link criado')}
                </p>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {!webhookConfig && (
                  <button
                    type="button"
                    onClick={handleCreateLink}
                    disabled={creatingLink || !clinicId}
                    className={primaryButton}
                  >
                    {creatingLink ? <Loader2 className="h-4 w-4 animate-spin" /> : <Webhook className="h-4 w-4" />}
                    Criar link
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleCopy}
                  disabled={!generatedUrl}
                  className={generatedUrl ? outlineButton : secondaryButton}
                >
                  <Clipboard className="h-4 w-4" />
                  Copiar
                </button>
                {webhookConfig && (
                  <button
                    type="button"
                    onClick={() => setConfirmRotateOpen(true)}
                    disabled={rotating || !clinicId}
                    className={secondaryButton}
                  >
                    {rotating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                    Regenerar
                  </button>
                )}
              </div>
            </section>

            <section className={`rounded-xl border ${panel} p-5`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3">
                  <Search className="mt-0.5 h-5 w-5 shrink-0 text-sminy-primary" />
                  <div>
                    <h2 className="text-sm font-semibold">Teste recebido</h2>
                    <p className={`mt-1 text-sm ${mutedText}`}>Depois de enviar um teste, atualize para carregar os dados.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleRefreshCapture}
                  disabled={!webhookConfig || refreshing || !clinicId}
                  className={webhookConfig && clinicId ? primaryButton : secondaryButton}
                >
                  {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  Atualizar
                </button>
              </div>

              {capturedPayload ? (
                <div className="mt-4 space-y-3">
                  <div className={`rounded-xl border p-3 text-sm ${
                    hasMappingIssue
                      ? isDark ? 'border-amber-400/30 bg-amber-500/10 text-amber-200' : 'border-amber-300 bg-amber-50 text-amber-700'
                      : 'border-sminy-success/30 bg-sminy-success/10 text-sminy-success'
                  }`}>
                    {hasMappingIssue
                      ? `Recebido em ${formatDateTime(webhookConfig?.last_captured_at)}. Confirme os campos para criar o lead.`
                      : `Recebido em ${formatDateTime(webhookConfig?.last_captured_at)} com ${payloadOptions.length} campo(s) detectado(s).`}
                    {hasMappingIssue && webhookConfig?.last_capture_error ? (
                      <span className="mt-1 block text-xs opacity-80">{webhookConfig.last_capture_error}</span>
                    ) : null}
                  </div>
                  <div className="grid gap-2">
                    {payloadOptions.slice(0, 5).map(option => (
                      <div key={option.path} className={`rounded-xl border px-3 py-2 ${theme.input}`}>
                        <p className="truncate text-sm font-medium">{option.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={`mt-4 rounded-xl border border-dashed p-4 text-sm ${theme.border} ${mutedText}`}>
                  Nenhum teste capturado.
                </div>
              )}
            </section>
          </div>

          <section className={`rounded-xl border ${panel} p-5`}>
            <div className="flex items-start gap-3">
              <Wand2 className="mt-0.5 h-5 w-5 shrink-0 text-sminy-primary" />
              <div>
                <h2 className="text-sm font-semibold">Confirmação dos campos</h2>
                <p className={`mt-1 text-sm ${mutedText}`}>Confirme os dados básicos usados para criar contato e lead no CRM.</p>
              </div>
            </div>

            <div className="mt-5 grid gap-4">
              {INTERNAL_FIELDS.map(field => {
                const fieldOptions = getOptionsForField(field.key);
                const selectedPath = fieldOptions.some(option => option.path === fieldMapping[field.key])
                  ? fieldMapping[field.key] || ''
                  : '';
                return (
                  <label key={field.key} className="block space-y-2">
                    <span className={`text-sm font-medium ${theme.secondaryText}`}>
                      {field.label}{field.required ? ' *' : ''}
                    </span>
                    <select
                      value={selectedPath}
                      onChange={event => updateMapping(field.key, event.target.value)}
                      disabled={fieldOptions.length === 0}
                      className={`w-full rounded-xl border p-3 text-sm transition-colors focus:ring-2 ${input}`}
                    >
                      <option value="">{field.required ? 'Selecione' : 'Não usar'}</option>
                      {fieldOptions.map(option => (
                        <option key={`${field.key}-${option.path}`} value={option.path}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                );
              })}
            </div>

            <label className="mt-4 block space-y-2">
              <span className={`text-sm font-medium ${theme.secondaryText}`}>
                Mídia *
              </span>
              <select
                value={fieldMapping.source_id_static || DEFAULT_CRM_SOURCE}
                onChange={event => setFieldMapping(prev => ({
                  ...prev,
                  source_id: undefined,
                  source_id_static: event.target.value,
                }))}
                className={`w-full rounded-xl border p-3 text-sm transition-colors focus:ring-2 ${input}`}
              >
                {CRM_SOURCE_OPTIONS.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>

            <div className={`mt-5 rounded-xl border p-4 ${theme.input}`}>
              <span className="block text-sm font-medium">Data de entrada</span>
              <span className={`mt-1 block text-sm ${mutedText}`}>Gerada automaticamente quando o lead entra no CRM.</span>
            </div>

            {testResult && (
              <div className={`mt-4 rounded-xl border p-3 ${theme.input}`}>
                <p className="text-sm font-medium">Prévia do CRM</p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  {[
                    ['Nome', testResult.name],
                    ['WhatsApp', testResult.phone],
                    ['Mídia', testResult.source_id || 'Não informada'],
                    ['Data de entrada', 'Automática'],
                  ].map(([label, value]) => (
                    <div key={String(label)} className={`rounded-xl border px-3 py-2 ${panel}`}>
                      <p className={`text-xs ${mutedText}`}>{label}</p>
                      <p className="mt-1 truncate text-sm font-medium">{String(value || '-')}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </section>
        </div>

        <section aria-labelledby="form-first-message-title" className={`rounded-xl border ${panel} p-5`}>
          <label className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <span id="form-first-message-title" className="block text-sm font-semibold">{officialCatalog.isOfficial === false && !messageConfig.official_template_id ? 'Mensagem inicial pelo agente' : 'Mensagem inicial'}</span>
              <span className={`mt-1 block text-sm ${mutedText}`}>Quando desativado, o lead entra no CRM sem primeiro envio automático.</span>
            </div>
            <input type="checkbox" aria-label="Enviar mensagem inicial" checked={messageConfig.send_initial_message} disabled={saving}
              onChange={event => setMessageConfig(prev => ({ ...prev, send_initial_message: event.target.checked }))}
              className={`h-5 w-5 shrink-0 rounded accent-sminy-primary focus:ring-sminy-primary ${theme.border}`} />
          </label>

          <FormOfficialTemplateSettings catalog={officialCatalog} config={messageConfig} onChange={setMessageConfig}
            payloadOptions={payloadOptions} fieldMapping={fieldMapping} onFieldMappingChange={setFieldMapping} disabled={saving} />

          <label className="mt-5 block space-y-2">
            <span className={`text-sm font-medium ${theme.secondaryText}`}>Contexto da campanha</span>
            <textarea value={messageConfig.campaign_context || ''} onChange={event => setMessageConfig(prev => ({ ...prev, campaign_context: event.target.value }))}
              maxLength={1000} rows={3} disabled={saving}
              className={`w-full resize-none rounded-xl border p-3 text-sm transition-colors focus:ring-2 ${input}`}
              placeholder="Ex.: Lead veio da campanha de implantes com avaliação gratuita." />
            {officialCatalog.isOfficial && <p className={`text-xs ${mutedText}`}>O contexto orienta o atendimento do agente, mas não altera o conteúdo aprovado do template.</p>}
          </label>
        </section>

        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => handleValidateMapping()}
            disabled={testing || payloadOptions.length === 0 || !clinicId}
            className={payloadOptions.length > 0 && clinicId ? outlineButton : secondaryButton}
          >
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Validar
          </button>
          <button
            type="button"
            onClick={handleActivate}
            disabled={saving || testing || payloadOptions.length === 0 || !clinicId || (messageConfig.send_initial_message && officialCatalog.loading)}
            className={primaryButton}
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isActive ? 'Salvar alterações' : 'Ativar integração'}
          </button>
        </div>

        {webhookConfig && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setConfirmRevokeOpen(true)}
              disabled={revoking || !clinicId}
              className={destructiveButton}
            >
              {revoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Revogar webhook
            </button>
          </div>
        )}
      </div>

      <ConfirmDeleteModal
        isOpen={confirmRotateOpen}
        onClose={() => setConfirmRotateOpen(false)}
        onConfirm={handleRotate}
        title="Regenerar link"
        message="O link atual deixará de funcionar imediatamente. Integrações externas precisarão usar o novo link."
        confirmText="Regenerar"
      />
      <ConfirmDeleteModal
        isOpen={confirmRevokeOpen}
        onClose={() => setConfirmRevokeOpen(false)}
        onConfirm={handleRevoke}
        title="Revogar webhook"
        message="O recebimento público de leads será interrompido até que um novo webhook seja salvo."
        confirmText="Revogar"
      />
    </div>
  );
};

export default FormLeadWebhookPage;
